import { useEffect, useRef } from 'react';
import { isNativeApp, setNativeMeetingSchedule } from 'common/webview';
import { MeetingRepository } from 'repository/meetingRepository';

/**
 * 네이티브 앱 첫 진입(또는 재설치 후) 시 서버에서 활성 모임 목록을 가져와
 * `SET_MEETING_SCHEDULE` 브릿지로 일괄 등록한다.
 *
 * - 딥링크로 홈을 거치지 않고 바로 특정 화면으로 들어와도 BackgroundGeolocation
 *   스케줄이 복구됨 (재설치 시 네이티브 메모리 증발 대비).
 * - 세션당 1회만 실행 (ProtectedRoute 마운트 × N 화면 이동에도 1회).
 * - 권위 소스: 서버의 `myLocationSharing`. localStorage 캐시는 미러링.
 *
 * 호출 위치: ProtectedRoute 안, 인증 확정 직후.
 */
let sessionSynced = false;

export const useNativeScheduleSync = (loggedIn: boolean) => {
    const triggeredRef = useRef(false);

    useEffect(() => {
        if (!loggedIn) return;
        if (!isNativeApp()) return;
        if (triggeredRef.current || sessionSynced) return;
        triggeredRef.current = true;
        sessionSynced = true;

        (async () => {
            try {
                const res: any = await MeetingRepository.onGetMyMeetings({ pageSize: 100 } as any);
                const body = res?.data || {};
                const inner = body?.data;
                const list: any[] = Array.isArray(inner)
                    ? inner
                    : ([
                        ...(inner?.today || []),
                        ...(inner?.ongoing || []),
                        ...(inner?.upcoming || []),
                        ...(inner?.list || []),
                    ]);
                const now = Date.now();
                let registered = 0;

                list.forEach((m) => {
                    const id = m.meetingId || m.id || m.meetingNo;
                    if (!id) return;
                    if (m.myAttendanceStatus !== 'ATTENDING') return;
                    const serverSharing = m.myLocationSharing === true;
                    const localSharing = localStorage.getItem(`locationSharing_${id}`) === 'true';
                    if (!serverSharing && !localSharing) return;

                    const startRaw = m.scheduleStartDateTime || m.schedule?.startDatetime;
                    const endRaw = m.scheduleEndDateTime || m.schedule?.endDatetime;
                    if (!startRaw) return;

                    const startTs = new Date(startRaw).getTime();
                    const endTs = endRaw
                        ? new Date(endRaw).getTime()
                        : startTs + 3 * 60 * 60 * 1000;
                    if (endTs < now) return; // 이미 종료

                    // 모임 좌표는 응답 body 의 다양한 키 형태에 대응 (latitude/longitude/lat/lng/meetingLat 등)
                    const lat = m.latitude ?? m.lat ?? m.meetingLat;
                    const lng = m.longitude ?? m.lng ?? m.lon ?? m.meetingLon ?? m.meetingLng;
                    setNativeMeetingSchedule(String(id), startTs, endTs, {
                        meetingLat: typeof lat === 'number' ? lat : Number(lat),
                        meetingLon: typeof lng === 'number' ? lng : Number(lng),
                    });
                    localStorage.setItem(`locationSharing_${id}`, 'true');
                    registered += 1;
                });

                if (registered > 0) {
                    console.log(`[NativeScheduleSync] 네이티브 측위 스케줄 ${registered}건 등록`);
                }
            } catch (e) {
                console.warn('[NativeScheduleSync] 실패', e);
                // 실패하면 sessionSynced 해제해서 다음 마운트에서 재시도 가능
                sessionSynced = false;
                triggeredRef.current = false;
            }
        })();
    }, [loggedIn]);
};
